import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { useSearchParams } from '@occc-app/hooks/useSearchParams';
import { usePaginationParams } from '@occc-app/hooks/usePaginationParams';
import { useDeleteKioskDefaultScreenMutation } from '@occc/graphql';

import { DefaultScreensListPage } from './default-screens-list-page.component';

import { TouchpointSearchForm } from '../../components/search-form.component';
import { useGetDefaultScreens } from '../../hooks/use-get-default-screens.hook';
import { routeCreator } from '../../touchpoint.router';
import {
  DEFAULT_SCREEN_DELETE_ERROR_MESSAGE,
  DEFAULT_SCREEN_DELETE_SUCCESS_MESSAGE,
  DEFAULT_SCREEN_PAGINATION_PARAMS,
  DEFAULT_SCREENS_SEARCH_PARAMS,
} from '../../touchpoint.constants';

export const DefaultScreensListPageContainer = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useSearchParams(DEFAULT_SCREENS_SEARCH_PARAMS);
  const [paginationInfo, onPageChange] = usePaginationParams(DEFAULT_SCREEN_PAGINATION_PARAMS);

  const [defaultScreenIdToDelete, setDefaultScreenIdToDelete] = useState<string | null>(null);

  const [deleteKioskDefaultScreen, { loading: loadingDeleteKioskDefaultScreen }] =
    useDeleteKioskDefaultScreenMutation();

  const onConfirmHandler = useCallback(async () => {
    if (!defaultScreenIdToDelete) {
      return;
    }

    await deleteKioskDefaultScreen({
      variables: { id: defaultScreenIdToDelete },
      onCompleted: () => {
        snackbar.success(DEFAULT_SCREEN_DELETE_SUCCESS_MESSAGE);
        setDefaultScreenIdToDelete(null);
      },
      onError: () => {
        snackbar.error(DEFAULT_SCREEN_DELETE_ERROR_MESSAGE);
        setDefaultScreenIdToDelete(null);
      },
      refetchQueries: ['kioskDefaultScreens'],
    });
  }, [deleteKioskDefaultScreen, defaultScreenIdToDelete]);

  const onCloseHandler = useCallback(() => {
    setDefaultScreenIdToDelete(null);
  }, []);

  const defaultScreenFilter = useMemo(() => {
    return {
      searchTerm: search || undefined,
    };
  }, [search]);

  const { loading, error, data, paginationProps } = useGetDefaultScreens({
    filter: defaultScreenFilter,
    paginationInfo,
    onPageChange,
  });

  const tableActions = useMemo(
    () => (
      <TouchpointSearchForm
        onSubmit={(values) => {
          setSearch(values.search);
        }}
        initialValues={{ search }}
      />
    ),
    [search, setSearch],
  );

  const handleEdit = useCallback(
    (defaultScreenId: string) => {
      navigate(routeCreator.defaultScreensUpdate(defaultScreenId));
    },
    [navigate],
  );

  const handleDelete = useCallback((defaultScreenId: string) => {
    setDefaultScreenIdToDelete(defaultScreenId);
  }, []);

  const defaultScreenNameToDelete = useMemo(() => {
    return (
      data?.find((defaultScreen) => defaultScreen.id === defaultScreenIdToDelete)?.name ?? null
    );
  }, [data, defaultScreenIdToDelete]);

  return (
    <DefaultScreensListPage
      loading={loading}
      error={error}
      data={data}
      paginationProps={paginationProps}
      tableActions={tableActions}
      onItemClick={handleEdit}
      onEdit={handleEdit}
      onDelete={handleDelete}
      defaultScreenNameToDelete={defaultScreenNameToDelete}
      defaultScreenIdToDelete={defaultScreenIdToDelete}
      onCloseDeleteDialog={onCloseHandler}
      onConfirmDelete={onConfirmHandler}
      loadingDeleteKioskDefaultScreen={loadingDeleteKioskDefaultScreen}
    />
  );
};
